import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, MapPin, TrendingUp, DollarSign } from 'lucide-react';
import { VendedorDetailedMetrics } from '@/types/vendedores';

interface VendedorParceirosTabProps {
  vendedor: VendedorDetailedMetrics;
}

export function VendedorParceirosTab({ vendedor }: VendedorParceirosTabProps) {
  const [searchTerm, setSearchTerm] = useState('');

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const filteredParceiros = vendedor.parceiros.filter(parceiro =>
    parceiro.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    parceiro.cidade?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    parceiro.estado?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Busca */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar parceiro por nome, cidade ou estado..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total de Parceiros</p>
              <p className="text-3xl font-bold">{vendedor.total_parceiros}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Parceiros Ativos</p>
              <p className="text-3xl font-bold text-green-600">{vendedor.parceiros_ativos}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Novos (30 dias)</p>
              <p className="text-3xl font-bold text-blue-600">{vendedor.parceiros_30_dias}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Parceiros */}
      <div className="space-y-3">
        {filteredParceiros.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">
                {searchTerm ? 'Nenhum parceiro encontrado com os critérios de busca' : 'Este vendedor ainda não possui parceiros'}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredParceiros.map((parceiro) => (
            <Card key={parceiro.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h4 className="font-semibold text-base sm:text-lg break-words">{parceiro.nome}</h4>
                      <Badge variant={parceiro.ativo ? 'default' : 'secondary'}>
                        {parceiro.ativo ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      <span>{parceiro.cidade}/{parceiro.estado}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{parceiro.total_leads}</p>
                    <p className="text-xs text-blue-600">Leads</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{parceiro.propostas_enviadas}</p>
                    <p className="text-xs text-green-600">Propostas</p>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">{parceiro.contratos_fechados}</p>
                    <p className="text-xs text-purple-600">Contratos</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <div className="flex items-center justify-center gap-1">
                      <DollarSign className="h-4 w-4 text-orange-600" />
                      <p className="text-sm font-bold text-orange-600">
                        {formatCurrency(parceiro.valor_contratos)}
                      </p>
                    </div>
                    <p className="text-xs text-orange-600">Valor Total</p>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Taxa de Conversão</span>
                  <div className="flex items-center gap-2">
                    <TrendingUp className={`h-4 w-4 ${parceiro.taxa_conversao >= 10 ? 'text-green-600' : 'text-yellow-600'}`} />
                    <span className={`font-bold ${parceiro.taxa_conversao >= 10 ? 'text-green-600' : 'text-yellow-600'}`}>
                      {parceiro.taxa_conversao}%
                    </span>
                  </div>
                </div>

                <div className="mt-2 pt-2 border-t flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Parceiros Rede</span>
                  <span className="text-sm font-semibold">{parceiro.total_parceiros_rede || 0}</span>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Contador */}
      {filteredParceiros.length > 0 && (
        <p className="text-sm text-muted-foreground text-center">
          Exibindo {filteredParceiros.length} de {vendedor.parceiros.length} parceiros
        </p>
      )}
    </div>
  );
}

